import { NavLink } from 'react-router-dom';
import { cn } from '@playerbase/ui';

import { useShell } from '../../shell/use-shell.js';

type SubNavItem = {
  to: string;
  label: string;
};

export function GameSettingsNav(): JSX.Element | null {
  const { activeStudio, activeGame } = useShell();
  if (activeGame === null) return null;

  const base = `/s/${activeStudio.shortId}/g/${activeGame.shortId}/game-settings`;
  const items: SubNavItem[] = [
    { to: `${base}/overview`, label: 'Game overview' },
    { to: `${base}/builds`, label: 'Builds' },
    { to: `${base}/milestones`, label: 'Milestones' },
    { to: `${base}/discord`, label: 'Discord connection' },
    { to: `${base}/notifications`, label: 'Notifications' },
  ];

  return (
    <nav
      aria-label="Game settings navigation"
      className={cn(
        'shrink-0 border-border bg-surface',
        'border-b md:w-56 md:border-b-0 md:border-r',
        // Independent scroll on desktop: nav stays put, content scrolls.
        'md:h-full md:overflow-y-auto',
      )}
    >
      {/* Section heading matches the shell-level SecondaryNav's top row
          so Game settings reads the same as the other section rails.
          Hidden on the mobile horizontal strip - the row of tabs is the
          heading there. */}
      <div className="hidden md:flex h-12 items-center border-b border-border px-4">
        <span className="font-display text-sm font-bold uppercase tracking-wider text-text">
          Game settings
        </span>
      </div>
      <ul className="flex gap-1 overflow-x-auto px-2 py-3 md:flex-col md:overflow-x-visible md:py-3">
        {items.map((item) => (
          <li key={item.to} className="md:w-full">
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'block whitespace-nowrap rounded-sm px-3 py-2 text-sm transition-colors duration-150',
                  'border-l-2 border-transparent',
                  isActive
                    ? 'bg-accent-bg text-accent border-l-accent'
                    : 'text-text-muted hover:bg-surface-2 hover:text-text',
                )
              }
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
